import useFacebookPixel from "@/utils/helpers/pixel-helpers/hooks/useFacebookPixel";
import useSnapchatPixel from "@/utils/helpers/pixel-helpers/hooks/useSnapchatPixel";
import {
  Gtag,
  GTagMaillistConversionEvent,
  GTagSearchEvent,
  GTagUserLoginEvent,
  GTagLeadEvent,
  GTagContactEvent,
  GTagCompleteRegistrationEvent,
} from "../analytics";
import {
  CartItemTypes,
  ICartItem_Product,
  ICartItemType,
} from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import {
  EventType,
  GtagActionField,
  IGTagUserData,
  Tracker,
} from "./helpers/interfaces";
import { getEventNameForTracker, convertToXFormat } from "./helpers/functions";
import { getAgentIdCookie } from "@/utils/functions/cookieOperations";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { useCurrencyCalc } from "@/hooks/useCurrencyCalc";
import useTiktokPixel from "../useTiktokPixel";
import useXPixel from "../useXPixel";
import { v4 as uuidv4 } from "uuid";
import { useAtom } from "jotai";
import { isSignedInAtom } from "@/hooks/authAtoms";
import { useGetProfileQuery } from "@/api/AppApi/services/auth";
import { toSHA256Hex } from "@/utils/functions/toSHA256Hex";
import { checkoutResultAtom } from "@/hooks/checkoutResultAtom";
import { getFirstAndLastName } from "@/utils/helpers/pixel-helpers/nameParts";

const useEventTrackers = () => {
  const company = useGetCompanyQuery();
  const { currency, getPriceString } = useCurrencyCalc({
    enable:
      company?.data?.result?.theme?.settings?.enable_currencies_converter ??
      true,
  });
  const { name } = company?.data?.result ?? {};
  const snapchatEvent = useSnapchatPixel();
  const facebookEvent = useFacebookPixel();
  const tiktokEvent = useTiktokPixel();
  const xEvent = useXPixel();

  // Extract custom X Pixel event IDs from company settings
  const settings = company?.data?.result?.theme?.settings;
  const xPixelEventIds = settings
    ? {
        x_page_view_event_id: settings.x_page_view_event_id,
        x_content_view_event_id: settings.x_content_view_event_id,
        x_add_to_cart_event_id: settings.x_add_to_cart_event_id,
        x_lead_event_id: settings.x_lead_event_id,
        x_purchase_event_id: settings.x_purchase_event_id,
        x_add_payment_info_event_id: settings.x_add_payment_info_event_id,
      }
    : undefined;

  const [isSignedIn] = useAtom(isSignedInAtom);
  const { data: userProfile } = useGetProfileQuery();
  const [checkoutResult] = useAtom(checkoutResultAtom);

  const prepareCustomerData = (providedUserData?: IGTagUserData) => {
    const customerData: any = {};

    if (isSignedIn && userProfile?.result) {
      const profile = userProfile.result;
      if (profile.email) {
        customerData.email = profile.email;
        customerData.email_hash = toSHA256Hex(profile.email);
      }
      if (profile.mobile) {
        customerData.phone = profile.mobile;
        customerData.phone_hash = toSHA256Hex(profile.mobile);
      }
      if (profile.name) {
        const { firstName, lastName } = getFirstAndLastName(profile.name);
        customerData.first_name = firstName;
        customerData.last_name = lastName;
        customerData.first_name_hash = toSHA256Hex(firstName);
        customerData.last_name_hash = toSHA256Hex(lastName);
      }
      if (profile.address?.city_name) {
        customerData.city = profile.address.city_name;
      }
      if (profile.address?.area_name) {
        customerData.area = profile.address.area_name;
      }
      if (profile.address?.country_name) {
        customerData.country = profile.address.country_name;
      }
    } else if (providedUserData) {
      if (providedUserData.email) {
        customerData.email = providedUserData.email;
        customerData.email_hash =
          providedUserData.email_hash || toSHA256Hex(providedUserData.email);
      }
      if (providedUserData.phone) {
        customerData.phone = providedUserData.phone;
        customerData.phone_hash =
          providedUserData.phone_hash || toSHA256Hex(providedUserData.phone);
      }
      if (providedUserData.name) {
        const { firstName, lastName } = getFirstAndLastName(
          providedUserData.name,
        );
        customerData.first_name = firstName;
        customerData.last_name = lastName;
        customerData.first_name_hash = toSHA256Hex(firstName);
        customerData.last_name_hash = toSHA256Hex(lastName);
      }
    }

    if (typeof window !== "undefined") {
      customerData.client_user_agent = navigator.userAgent;
      const agentId = getAgentIdCookie();
      if (agentId) {
        customerData.external_id = agentId;
      }
    }

    if (checkoutResult?.cities_names?.length) {
      customerData.city = checkoutResult.cities_names.join(",");
    }
    if (checkoutResult?.areas_names?.length) {
      customerData.area = checkoutResult.areas_names.join(",");
    }
    if (checkoutResult?.countries_names?.length) {
      customerData.country = checkoutResult.countries_names.join(",");
    }

    return customerData;
  };

  const eventTrackers = (
    event: EventType,
    rawItems: ICartItemType[],
    data: GtagActionField,
    userdata?: IGTagUserData,
    eventID?: string,
  ) => {
    // Callers build this with `items.find(...)!`, so a miss yields `[undefined]`
    // and the branches below throw on `item.type`. Analytics must never break
    // commerce — drop the holes.
    const items = (rawItems ?? []).filter(Boolean);

    const isNonEcommerce = [
      EventType.search,
      EventType.lead,
      EventType.contact,
      EventType.complete_registration,
    ].includes(event);

    const getItemId = (item: ICartItemType): string => {
      const id =
        item.type === CartItemTypes.product
          ? ((item as ICartItem_Product)?.others?.parent_id ?? item.inner_id)
          : item.inner_id;
      return id?.toString() ?? "";
    };


    // Prefer the currency explicitly passed in data, fall back to atom, then default
    const resolvedCurrency =
      data?.currency?.toLowerCase() || currency.code?.toLowerCase() || "kwd";

    const SnapChatEventData: any = isNonEcommerce
      ? {
          query: data?.search_string,
        }
      : {
          currency: resolvedCurrency,
          price: data?.skipConversion
            ? +(data?.value ?? 0)
            : +getPriceString(data?.value!),
          transaction_id: data?.transaction_id,
          item_ids: items?.map(getItemId),
          number_items: items?.length,
        };
    if (!!userdata) {
      const { firstName, lastName } = getFirstAndLastName(userdata.name ?? "");
      SnapChatEventData["user_email"] = userdata.email;
      SnapChatEventData["user_hashed_email"] = userdata.email_hash;
      SnapChatEventData["user_phone_number"] = userdata.phone;
      SnapChatEventData["user_hashed_phone_number"] = userdata.phone_hash;
      SnapChatEventData["firstname"] = firstName;
      SnapChatEventData["lastname"] = lastName;
    }

    const customerData = prepareCustomerData(userdata);

    const customInvoiceData: any = {};
    // if (data?.transaction_id)
    //   customInvoiceData.invoice_id = data.transaction_id;
    // if (data?.invoice_number)
    //   customInvoiceData.invoice_number = data.invoice_number;
    // if (data?.invoice_date) customInvoiceData.invoice_date = data.invoice_date;
    // if (data?.invoice_time) customInvoiceData.invoice_time = data.invoice_time;
    if (data?.event_name) customInvoiceData.invoice_id = data.event_name; //!! note it come from back end and it's the invoice id

    const facebookEventData: any = isNonEcommerce
      ? {
          ...customerData,
          ...customInvoiceData,
          search_string: data?.search_string,
        }
      : {
          // Standard Facebook e-commerce parameters
          currency: resolvedCurrency,
          value: data?.skipConversion
            ? +(data?.value ?? 0)
            : +getPriceString(data?.value!),
          content_ids: items?.map(getItemId),
          content_type: "product",
          contents: items?.map((item) => ({
            id: getItemId(item),
            quantity: item.quantity ?? 1,
          })),
          num_items: items?.reduce(
            (sum, item) => sum + (item.quantity ?? 1),
            0,
          ),
          // Customer data for advanced matching
          ...customerData,
          // Custom invoice data as event parameters
          ...customInvoiceData,
        };
    // TikTok Pixel 2.0 shape. It ignores Meta's `content_ids` (it wants
    // `contents`), requires an UPPERCASE ISO-4217 currency, and rejects events
    // carrying raw customer PII in the properties — identity goes through
    // ttq.identify() below, never through the event payload.
    const tiktokEventData: any = isNonEcommerce
      ? {
          query: data?.search_string,
        }
      : {
          currency: resolvedCurrency.toUpperCase(),
          value: data?.skipConversion
            ? +(data?.value ?? 0)
            : +getPriceString(data?.value!),
          content_type: "product",
          contents: items?.map((item) => ({
            content_id: getItemId(item),
            content_type: "product",
            content_name: item?.parent?.name ?? item.name,
            price: +getPriceString(item.price),
            quantity: item.quantity ?? 1,
          })),
          order_id: data?.transaction_id,
        };
    if (
      ![EventType.remove_from_cart, EventType.add_shipping_info].includes(event)
    )
      snapchatEvent.trackEvent(
        getEventNameForTracker(event, Tracker.SCP),
        SnapChatEventData,
      );

    facebookEvent.trackEvent(
      getEventNameForTracker(event, Tracker.FBP),
      facebookEventData,
      eventID ?? facebookEventData?.invoice_id,
    );

    // TikTok has no standard event for these — sending them produces junk
    // custom events that can't be used for campaign optimisation.
    const tiktokUnsupported = [
      EventType.remove_from_cart,
      EventType.add_shipping_info,
      EventType.userLogin,
    ];
    if (!tiktokUnsupported.includes(event)) {
      // Advanced matching must land before the event, not inside it.
      tiktokEvent.identifyUser(customerData);
      tiktokEvent.trackEvent(
        getEventNameForTracker(event, Tracker.TIK),
        tiktokEventData,
        eventID ?? facebookEventData?.invoice_id,
      );
    }

    // X (Twitter) Pixel tracking
    const xEventData = convertToXFormat(
      items,
      {
        currency: resolvedCurrency.toUpperCase(),
        value: data?.skipConversion
          ? +(data?.value ?? 0)
          : +getPriceString(data?.value!),
        search_string: data?.search_string,
        description: data?.event_name,
      },
      customerData,
    );
    const xEventId = eventID ?? facebookEventData?.invoice_id;
    xEvent.trackEvent(
      getEventNameForTracker(event, Tracker.X, xPixelEventIds),
      xEventData,
      xEventId,
    );
    if (!isNonEcommerce)
      Gtag(
        getEventNameForTracker(event, Tracker.GTAG),
        items,
        data,
        name ?? "",
        getPriceString,
        currency.code,
        customerData,
      );
  };

  // X (Twitter) Pixel - User Login Event Tracker
  const XUserLoginEventTracker = (
    isLoggedIn: boolean,
    userdata: IGTagUserData,
  ) => {
    if (!isLoggedIn) return;
    const xEventId = uuidv4();
    xEvent.trackEvent(
      getEventNameForTracker(EventType.userLogin, Tracker.X, xPixelEventIds),
      {
        email_address: userdata.email,
        phone_number: userdata.phone,
        description: "User Login",
      },
      xEventId,
    );
  };

  const userLoginEventTracher = (
    isSignedIn: boolean,
    userdata: IGTagUserData,
  ) => {
    XUserLoginEventTracker(isSignedIn, userdata);
    return GTagUserLoginEvent(isSignedIn, userdata);
  };
  const GTagSearchEventTracher = (
    search: string,
    is_found: boolean,
    eventID?: string,
  ) => {
    eventTrackers(
      EventType.search,
      [],
      { value: 0, search_string: search },
      undefined,
      eventID,
    );
    return GTagSearchEvent(search, is_found ? "found" : "not found");
  };
  const GTagMaillistConversionEventTracher = (email: string) => {
    return GTagMaillistConversionEvent(email);
  };
  const GTagLeadEventTracher = (userdata: IGTagUserData) => {
    return GTagLeadEvent(userdata);
  };
  const GTagContactEventTracher = (userdata: IGTagUserData) => {
    return GTagContactEvent(userdata);
  };
  const GTagCompleteRegistrationEventTracher = (userdata: IGTagUserData) => {
    return GTagCompleteRegistrationEvent(userdata);
  };

  return {
    eventTrackers,
    userLoginEventTracher,
    GTagMaillistConversionEventTracher,
    GTagSearchEventTracher,
    GTagLeadEventTracher,
    GTagContactEventTracher,
    GTagCompleteRegistrationEventTracher,
  };
};

export default useEventTrackers;

export { getFirstAndLastName };
